import React, { useEffect, useMemo, useRef, useState } from "react";

// El formulario de lote no guarda datos hasta que el usuario confirma el resumen.
const normalized = (value) => String(value ?? "").normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
const formatXP = (value) => new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2 }).format(Number(value));
const numericSeason = (value) => /^\d+$/.test(String(value ?? ""));
const regularSeason = (item) => numericSeason(item.season) && (!item.type || item.type === "escenario");
const numberCode = (item) => numericSeason(item.season) && /^\d+$/.test(String(item.number ?? ""))
  ? `${Number(item.season)}-${String(item.number).padStart(2, "0")}` : "";
const label = (item) => `#${item.season}-${item.number}`;
const isValidXP = (value) => /^\d+$/.test(String(value))
  && Number(value) >= 1 && Number(value) <= 999999;

export default function TrackerBulkCredits({
  character, scenarios, existingCredits, busy, onSave, onStatusChange, step, onStepChange, formId,
}) {
  const [season, setSeason] = useState("all");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [acknowledged, setAcknowledged] = useState(false);
  const nextKey = useRef(1);
  const initial = existingCredits.find((credit) => credit.kind === "initial");
  const totalCents = rows.reduce((sum, row) =>
    sum + (Number.isFinite(Number(row.experience)) ? Math.round(Number(row.experience) * 100) : 0), 0);
  const currentCents = Math.round(Number(character.experience) * 100);
  const resultingXP = (currentCents + totalCents) / 100;
  const resultingLevel = Math.floor(resultingXP / 12) + 1;
  const valid = rows.length > 0 && rows.every((row) => isValidXP(row.experience));

  useEffect(() => {
    onStatusChange({
      count: rows.length, valid, acknowledged,
      addedXP: totalCents / 100, resultingXP, resultingLevel, hasInitial: Boolean(initial),
    });
  }, [onStatusChange, rows.length, valid, acknowledged,
    totalCents, resultingXP, resultingLevel, initial]);

  const seasons = useMemo(() => [...new Set(
    scenarios.filter(regularSeason).map((item) => Number(item.season)),
  )].sort((a, b) => a - b), [scenarios]);

  const matches = useMemo(() => scenarios.filter((item) => {
    if (season === "other" && regularSeason(item)) return false;
    if (season !== "all" && season !== "other" && (
      !regularSeason(item) || Number(item.season) !== Number(season)
    )) return false;
    const q = normalized(search);
    if (!q) return true;
    const code = numberCode(item);
    return [item.name, item.season, item.number, item.type, label(item), code,
      code && `S${code}`, code && `S${code.split("-")[0].padStart(2, "0")}-${code.split("-")[1]}`]
      .some((part) => normalized(part).includes(q));
  }).sort((a, b) => {
    const as = regularSeason(a);
    const bs = regularSeason(b);
    if (as !== bs) return as ? -1 : 1;
    const bySeason = as
      ? Number(a.season) - Number(b.season)
      : String(a.season).localeCompare(String(b.season), "es", { numeric: true });
    if (bySeason) return bySeason;
    return String(a.number || "").localeCompare(String(b.number || ""), "es", { numeric: true })
      || String(a.name).localeCompare(String(b.name), "es");
  }), [scenarios, season, search]);
  const visible = matches.slice(0, 75);

  const toggle = (item) => {
    if (busy) return;
    setRows((current) => {
      if (current.some((row) => String(row.scenario) === String(item.id))) {
        return current.filter((row) => String(row.scenario) !== String(item.id));
      }
      if (current.length >= 100) return current;
      return [...current, {
        key: nextKey.current++, scenario: item.id, name: item.name,
        code: label(item), experience: "4", notes: "",
      }];
    });
  };
  const change = (key, property, value) => {
    setRows((current) => current.map((row) => row.key === key
      ? { ...row, [property]: value } : row));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (busy || !valid) return;
    if (step === 1) {
      onStepChange(2);
      return;
    }
    if (step === 2) {
      setAcknowledged(false);
      onStepChange(3);
      return;
    }
    if (initial && !acknowledged) return;
    await onSave({ credits: rows.map(({ scenario, experience, notes }) =>
      ({ scenario, experience, notes })) });
  };

  return <form id={formId} className={`tracker-bulk-form tracker-bulk-form-step-${step}`} onSubmit={submit}>
    <div className="tracker-bulk-steps" aria-label="Paso de carga múltiple">
      <span className={step === 1 ? "tracker-bulk-step-active" : ""} aria-current={step === 1 ? "step" : undefined}>1. Seleccionar</span>
      <span className="tracker-bulk-step-arrow" aria-hidden="true">→</span>
      <span className={step === 2 ? "tracker-bulk-step-active" : ""} aria-current={step === 2 ? "step" : undefined}>2. Configurar XP</span>
      <span className="tracker-bulk-step-arrow" aria-hidden="true">→</span>
      <span className={step === 3 ? "tracker-bulk-step-active" : ""} aria-current={step === 3 ? "step" : undefined}>3. Confirmar</span>
    </div>
    {step === 3 ? <>
    <div className="tracker-bulk-review" aria-label="Revisar créditos">
      <h3>Confirmar carga múltiple</h3>
      <p>Vas a añadir <strong>{rows.length} {rows.length === 1 ? "crédito" : "créditos"}</strong> a <strong>{character.name}</strong>.</p>
      <div className="tracker-bulk-confirm-stats">
        <span>XP nuevas: <strong>{formatXP(totalCents / 100)}</strong></span>
        <span>XP resultante: <strong>{formatXP(resultingXP)}</strong></span>
        <span>Nivel previsto: <strong>{resultingLevel}</strong></span>
      </div>
      {initial && <div className="tracker-bulk-warning">
        <strong>Este personaje ya tiene {formatXP(initial.experience)} XP iniciales.</strong>
        <p>Comprueba que los créditos anteriores no estén incluidos en esa experiencia para no contabilizarla dos veces.</p>
        <label className="tracker-bulk-acknowledge">
          <input type="checkbox" checked={acknowledged} disabled={busy}
            onChange={(event) => setAcknowledged(event.target.checked)} />
          He comprobado que no voy a contabilizar dos veces la misma XP.
        </label>
      </div>}
      <p className="tracker-bulk-confirm-description">Todos los créditos se guardarán juntos. Si alguno no es válido, no se guardará ninguno. No se modificará el historial Player/GM.</p>
    </div>
    </> : step === 1 ? <>
      <div className="tracker-bulk-filters">
        <label>Temporada
          <select value={season} disabled={busy} onChange={(event) => setSeason(event.target.value)}>
            <option value="all">Todas las temporadas</option>
            {seasons.map((value) => <option key={value} value={String(value)}>Temporada {value}</option>)}
            <option value="other">Otros contenidos (quests, módulos…)</option>
          </select>
        </label>
        <label>Buscar
          <input type="search" value={search} disabled={busy} autoComplete="off"
            placeholder="Nombre o código, ej. 1-07"
            onChange={(event) => setSearch(event.target.value)} />
        </label>
      </div>
      <div className="tracker-bulk-results" role="list" aria-label="Escenarios disponibles">
        {visible.map((item) => {
          const selected = rows.some((row) => String(row.scenario) === String(item.id));
          return <div className="tracker-bulk-result" role="listitem" key={item.id}>
            <button type="button" className={selected ? "tracker-bulk-choice tracker-bulk-choice-selected" : "tracker-bulk-choice"}
              aria-pressed={selected} disabled={busy || (!selected && rows.length >= 100)}
              aria-label={`${selected ? "Deseleccionar" : "Seleccionar"} escenario: ${label(item)} ${item.name}`}
              onClick={() => toggle(item)}>
              <span className="tracker-bulk-result-title"><strong>{label(item)}</strong><span>{item.name}</span></span>
              <span className="tracker-bulk-choice-mark" aria-hidden="true">{selected ? "✓" : ""}</span>
            </button>
          </div>;
        })}
        {!visible.length && <p className="tracker-bulk-no-results">No hay escenarios que coincidan con los filtros.</p>}
      </div>
      {rows.length === 100 && <small className="tracker-bulk-results-more">
        Máximo de 100 crónicas por lote.
      </small>}
    </> : <>
      <div className="tracker-bulk-list-heading">
        <h3>Créditos preparados</h3>
        <small>{rows.length} de 100</small>
      </div>
      {!rows.length ? <div className="tracker-bulk-empty">
        <p>Selecciona escenarios para añadirlos a esta lista.</p>
        <small>Podrás ajustar los XP, añadir notas o quitar cualquiera antes de guardar.</small>
      </div> : <div className="tracker-bulk-list" aria-label="Créditos preparados">
        {rows.map((row) => <article key={row.key} className="tracker-bulk-item">
          <div className="tracker-bulk-item-top">
            <div className="tracker-bulk-item-name">
              <strong>{row.name} <span className="tracker-bulk-item-code">({row.code})</span></strong>
            </div>
            <button type="button" className="tracker-bulk-remove" disabled={busy}
              aria-label={`Quitar crédito: ${row.name}`}
              onClick={() => setRows((current) => current.filter((entry) => entry.key !== row.key))}>×</button>
          </div>
          <div className="tracker-bulk-xp-line">
            <div className="tracker-bulk-xp-controls">
              {[2, 4, 12].map((amount) => <button key={amount} type="button"
                className={Number(row.experience) === amount ? "tracker-xp-preset tracker-xp-selected" : "tracker-xp-preset"}
                aria-pressed={Number(row.experience) === amount} disabled={busy}
                onClick={() => change(row.key, "experience", String(amount))}>{amount}</button>)}
              <label className="tracker-bulk-xp-custom">
                <span className="visually-hidden">XP para {row.name}</span>
                <input type="number" inputMode="numeric" min="1" max="999999" step="1"
                  value={row.experience} disabled={busy}
                  onChange={(event) => change(row.key, "experience", event.target.value)}
                  aria-label={`XP para ${row.name}`} />
                <span>XP</span>
              </label>
            </div>
          </div>
          <details className="tracker-bulk-notes">
            <summary>{row.notes ? "Notas añadidas" : "Añadir notas"}</summary>
            <textarea value={row.notes} rows="2" disabled={busy} aria-label={`Notas para ${row.name}`}
              onChange={(event) => change(row.key, "notes", event.target.value)}
              placeholder="Información de esta crónica..." />
          </details>
        </article>)}
      </div>}
      {initial && <div className="tracker-bulk-warning" role="note">
        <strong>Este personaje ya tiene experiencia inicial ({formatXP(initial.experience)} XP).</strong>
        <p>Si registras crónicas que ya estaban incluidas, sumarás XP dos veces.
          Lo volveremos a comprobar antes de guardar.</p>
      </div>}
      {!valid && rows.length > 0 && <small className="tracker-bulk-invalid" role="alert">
        Revisa los XP: cada crédito debe tener un número entero entre 1 y 999999.
      </small>}
    </>}
  </form>;
}
